import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiService, CHECKOUT_API_CONFIG } from '../../services/apiService';
import { API_PATHS } from '../../utils/apiPaths';
import PaymentStatusCard, {
  resolvePaymentStatusVariant,
} from './PaymentStatusCard';
import { formatCurrency } from '../../utils/helpers';
import { isEmbeddedCheckout, postCheckoutMessage } from '../checkout/checkoutEmbed';

type PaymentStatus = {
  transactionId: string;
  status: string;
  amount: number;
  currency: string;
  acquirerReference: string;
  message: string;
  reason: string;
  merchantRedirectUrl: string;
  redirectTimerSeconds: number | null;
  logoUrl: string;
  /** Merchant.directMerchantRedirectEnabled: skip our status page and go straight to
   * merchantRedirectUrl once the payment resolves. */
  directMerchantRedirect: boolean;
};

type LoadState = 'loading' | 'ready' | 'error';

// Used only when the merchant never set a redirect timer (Merchant.redirectTimerSeconds is null)
// — otherwise the real value from PaymentStatusResponse.redirectTimerSeconds wins, below.
const AUTO_REDIRECT_SECONDS = 5;

function unwrapPaymentStatusResponse(res: unknown, fallbackTransactionId: string): PaymentStatus {
  let data: Record<string, unknown> =
    res && typeof res === 'object' ? (res as Record<string, unknown>) : {};

  if (data.data && typeof data.data === 'object' && !('transactionId' in data)) {
    data = data.data as Record<string, unknown>;
  }

  return {
    transactionId: String(data.transactionId ?? fallbackTransactionId),
    status: String(data.status ?? 'UNKNOWN'),
    amount: Number(data.amount ?? 0),
    currency: String(data.currency ?? ''),
    acquirerReference: String(data.acquirerReference ?? '—'),
    message: String(data.message ?? '—'),
    reason: String(data.reason ?? ''),
    merchantRedirectUrl: String(data.merchantRedirectUrl ?? ''),
    redirectTimerSeconds:
      data.redirectTimerSeconds === undefined || data.redirectTimerSeconds === null
        ? null
        : Number(data.redirectTimerSeconds),
    logoUrl: String(data.logoUrl ?? ''),
    directMerchantRedirect: Boolean(data.directMerchantRedirect),
  };
}

// A bare, unbranded white screen with a neutral spinner. Used ONLY on the direct-merchant-redirect
// path so the shopper never sees our branded "Your payment is being processed" status card before we
// forward them to the merchant. The normal (non-direct-redirect) flow keeps its branded loading card.
const PlainWhiteLoader: React.FC = () => (
  <div
    role="status"
    aria-label="Processing"
    style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      alignItems: 'center',
      justifyContent: 'center',
      background: '#ffffff',
    }}
  >
    <span
      style={{
        width: 36,
        height: 36,
        border: '3px solid #e5e7eb',
        borderTopColor: '#9ca3af',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'mf-plain-spin 0.8s linear infinite',
      }}
    />
    <span style={{ color: '#6b7280', fontSize: 14 }}>Processing…</span>
    <style>{'@keyframes mf-plain-spin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

const PaymentRedirectStatusPage: React.FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const embedded = isEmbeddedCheckout();
  // Direct-merchant-redirect hint, set by the backend on the status-page URL (?dr=1) ONLY for
  // merchants with directMerchantRedirectEnabled. Lets us paint the plain white loader from the very
  // first frame — before the status call returns and confirms the flag — so the shopper never sees
  // our branded "Your payment is being processed" card. Normal merchants have no ?dr=1 → unchanged.
  // Deliberately NOT gated on !embedded. A direct-redirect merchant embeds us inside their own
  // page, so the embedded case is the one that matters most: showing our branded card there is
  // exactly the "moneyeu appears mid-flow" the toggle exists to prevent.
  const directRedirectHint = (() => {
    try {
      return new URLSearchParams(window.location.search).get('dr') === '1';
    } catch {
      return false;
    }
  })();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState('');
  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');

//for HPP iframe agnostic redirect
  // An HPP shopper must not be left looking at a result page nested inside an acquirer's frame, so
  // this breaks out to the top. But for a direct-redirect merchant (?dr=1) breaking out is the bug:
  // it drags their whole page to login.moneyeu.com purely to show a loader that then forwards to
  // their own return URL. Those stay in the frame, and the redirect effect below navigates it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const explicitlyEmbedded = params.get('embed') === '1' || params.get('embed') === 'true';
    let nested: boolean;
    try {
      nested = window.self !== window.top;
    } catch {
      nested = true;
    }
    if (nested && !explicitlyEmbedded && !directRedirectHint && window.top) {
      window.top.location.href = window.location.href;
    }
  }, [directRedirectHint]);

  const fetchStatus = async (id: string) => {
    // Carry the super-admin replay token (?h=) through to the status endpoint: a finished
    // transaction's status is blocked server-side without it ("This transaction is already
    // finished."). A normal shopper has no token and, within the grace window, still sees their
    // result; only a later replay is refused.
    const replayHash = new URLSearchParams(window.location.search).get('h');
    const url = replayHash
      ? `${API_PATHS.PAYMENT_REDIRECT_STATUS}/${encodeURIComponent(id)}?h=${encodeURIComponent(replayHash)}`
      : `${API_PATHS.PAYMENT_REDIRECT_STATUS}/${encodeURIComponent(id)}`;
    const res = await apiService.get<unknown>(
      url,
      { ...CHECKOUT_API_CONFIG, withCredentials: false },
    );
    return unwrapPaymentStatusResponse(res, id);
  };

  useEffect(() => {
    if (!transactionId) {
      setLoadError('Missing transaction ID.');
      setLoadState('error');
      return;
    }

    (async () => {
      try {
        setStatus(await fetchStatus(transactionId));
        setLoadState('ready');
      } catch (err: any) {
        setLoadError(
          err.response?.data?.message || 'Unable to load payment status for this transaction.',
        );
        setLoadState('error');
      }
    })();
  }, [transactionId]);

  useEffect(() => {
    if (!embedded) return;
    document.documentElement.classList.add('checkout-embedded');
    postCheckoutMessage({ type: 'CHECKOUT_READY' });
    return () => document.documentElement.classList.remove('checkout-embedded');
  }, [embedded]);

  useEffect(() => {
    if (!embedded || loadState !== 'error') return;
    postCheckoutMessage({ type: 'CHECKOUT_ERROR', message: loadError });
  }, [embedded, loadState, loadError]);

  useEffect(() => {
    if (!embedded || !status) return;
    postCheckoutMessage({
      type: 'PAYMENT_RESULT',
      transactionId: status.transactionId,
      status: status.status,
      message: status.message,
    });
  }, [embedded, status]);

  // This page is where a checkout session actually lands once a payment resolves — CheckoutPage's
  // own "paid" state is unreachable whenever the response carries a redirectUrl, which is true for
  // essentially every completed/declined live payment (see CheckoutPage.buildPaymentProcessResponse
  // usage). navigateForRedirect (CheckoutPage.tsx) carries parentOrigin forward onto this page's URL
  // when embedded, so the moneyeu.checkout.result message documented in ApiDocumentation.tsx is
  // actually posted from here — the page a partner's iframe is genuinely showing when the result
  // becomes final — not just from CheckoutPage, which very often never reaches that state at all.
  const hasPostedIframeResultRef = useRef(false);
  useEffect(() => {
    if (!status || window.self === window.top) return;
    if (hasPostedIframeResultRef.current) return;

    const parentOrigin = new URLSearchParams(window.location.search).get('parentOrigin');
    if (!parentOrigin) return;

    const variant = resolvePaymentStatusVariant(status.status);
    if (variant === 'pending') return;

    hasPostedIframeResultRef.current = true;
    try {
      window.parent.postMessage(
        {
          type: 'moneyeu.checkout.result',
          status: status.status,
          outcome: variant === 'success' ? 'success' : 'error',
          transactionId: status.transactionId,
          amount: status.amount,
          currency: status.currency,
          message: status.message,
          reason: status.reason || null,
          acquirerReference: status.acquirerReference,
        },
        parentOrigin,
      );
    } catch {
      // Malformed/mismatched parentOrigin — nothing more we can do safely.
    }
  }, [status]);

  // Direct merchant redirect (Merchant.directMerchantRedirectEnabled): the merchant opted out of our
  // hosted status page, so once the payment resolves we send the shopper STRAIGHT to their return
  // URL. We still let the status endpoint run on every poll — it drives every acquirer's
  // reconcileIfPending, so the outcome is persisted before we navigate away (plus webhooks and the
  // schedulers remain the backstops). While the payment is still pending we keep polling here, since
  // the status card that normally re-polls isn't rendered in this mode. Applies in the embedded
  // (merchant-iframe) flow too — that is the flow the toggle exists for, and leaving the frame on
  // the plain loader with nothing to navigate it would strand the shopper. Only when a return URL
  // is actually configured.
  useEffect(() => {
    if (!status) return;
    // The directMerchantRedirect guard below is what keeps every other embedded merchant on the
    // old path (they postMessage instead of navigating) — so this no longer bails on `embedded`.
    // A direct-redirect merchant's frame must actually reach their return URL, otherwise the
    // plain loader above spins forever inside their iframe.
    if (!status.directMerchantRedirect || !status.merchantRedirectUrl) return;
    if (resolvePaymentStatusVariant(status.status) === 'pending') {
      const t = setTimeout(() => {
        fetchStatus(status.transactionId).then(setStatus).catch(() => {});
      }, 4000);
      return () => clearTimeout(t);
    }
    window.location.replace(status.merchantRedirectUrl);
  }, [status, embedded]);

  const handleRefreshStatus = async () => {
    if (!transactionId) return;

    setRefreshing(true);
    setRefreshError('');
    try {
      setStatus(await fetchStatus(transactionId));
    } catch (err: any) {
      setRefreshError(
        err.response?.data?.message || 'Unable to refresh payment status. Please try again.',
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (loadState === 'loading') {
    // Direct-redirect merchants get the plain white loader from the first paint (hint on the URL);
    // everyone else keeps the branded loading card.
    return directRedirectHint ? (
      <PlainWhiteLoader />
    ) : (
      <PaymentStatusCard variant="loading" embedded={embedded} />
    );
  }

  if (loadState === 'error') {
    return (
      <PaymentStatusCard
        variant="error"
        title="Status unavailable"
        message={loadError}
        logoUrl={status?.logoUrl ?? null}
        embedded={embedded}
      />
    );
  }

  if (!status) return null;

  // Merchant opted out of our hosted status page: show a bare WHITE loader (not our branded status
  // card) while the effect above navigates the shopper on to their return URL. Only this
  // direct-redirect branch is affected; the normal flow keeps its branded loading/status cards.
  if (status.directMerchantRedirect && status.merchantRedirectUrl) {
    return <PlainWhiteLoader />;
  }

  const variant = resolvePaymentStatusVariant(status.status);

  // Only offer to auto-redirect once the payment has actually reached a final state, and only
  // when the merchant configured their own return URL — otherwise the customer simply stays on
  // this status page, per the merchant's own (lack of) configuration.
  const redirectConfig =
    !embedded && variant !== 'pending' && status.merchantRedirectUrl
      ? {
          timerSeconds:
            status.redirectTimerSeconds && status.redirectTimerSeconds > 0
              ? status.redirectTimerSeconds
              : AUTO_REDIRECT_SECONDS,
          redirectUrl: status.merchantRedirectUrl,
        }
      : undefined;

  return (
    <PaymentStatusCard
      variant={variant}
      message={refreshError || undefined}
      transactionId={status.transactionId}
      amount={formatCurrency(status.amount, status.currency, {
        includeCode: Boolean(status.currency),
      })}
      statusText={status.status}
      reason={status.reason || undefined}
      redirect={redirectConfig}
      logoUrl={status.logoUrl}
      embedded={embedded}
      action={{
        label: refreshing ? 'Refreshing…' : 'Refresh',
        hint: 'Refresh payment status',
        onClick: handleRefreshStatus,
        disabled: refreshing,
        autoTriggerAfterMs: variant === 'pending' ? 10000 : undefined,
      }}
    />
  );
};

export default PaymentRedirectStatusPage;
